import React from 'react';
import PropTypes from 'prop-types';
import { deviceType } from 'detect-it';
import ReactSwipe from 'react-swipe';
import ChevronRight from 'mdi-react/ChevronRightIcon';
import ChevronLeft from 'mdi-react/ChevronLeftIcon';
import { AppContext } from '@prisjakt/ui-app-context';
import { HorizontalScroll } from 'src/components/horizontal-scroll';
import { noop } from '@prisjakt/ui-base';
import { CarouselItem } from './carousel-item';
import {
  CarouselWrapper,
  SwitchButton,
  ThumbnailWrapper,
  CarouselSlide,
  Indicator,
  IndicatorWrapper,
  Thumbnail,
} from '../../styles';

export const SWIPE = 'swipe';
export const THUMBNAIL = 'thumbnail';
export const DOT = 'dot';

class Carousel extends React.Component {
  disableOnChange = false;

  constructor(props) {
    super(props);
    this.state = {
      currentPosition: 0,
    };
  }

  componentDidMount() {
    if (this.props.focus) {
      this.carousel.focus();
    }

    if (this.props.position !== null) {
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({ currentPosition: this.props.position });
    }
  }

  static deriveStateFromProps = nextProps => {
    this.setState({ currentPosition: nextProps.position });
  };

  static getSwipeStyles() {
    if (!ReactSwipe.defaultProps.style.wrapper) {
      throw new Error('ReactSwipe is missing defaultProps.style.wrapper');
    }

    return {
      ...ReactSwipe.defaultProps.style,
      wrapper: { ...ReactSwipe.defaultProps.style.wrapper, display: 'flex' },
    };
  }

  setPosition(newPosition, type) {
    this.props.onChange(newPosition, type);

    // Use this flag to make sure we don't send
    // two onChange events when adjusting slide
    this.disableOnChange = true;
    // React swipe needs it's own emitted position to work
    // correctly if it uses more slides behind the scenes
    this.reactSwipe.slide(newPosition);

    this.setState({
      currentPosition: newPosition,
    });
  }

  handleOnClick(e) {
    if (this.indicatorWrapper) {
      if (
        e.target === this.indicatorWrapper ||
        this.indicatorWrapper.contains(e.target)
      ) {
        return;
      }
    }

    this.props.onClick();
  }

  handleKeyDown(e) {
    const [ENTER, SPACE, LEFT, RIGHT] = [13, 32, 37, 39];

    switch (e.keyCode) {
      case SPACE:
        // Prevent default scroll behavior when space is hit
        e.preventDefault();
        this.props.onClick();
        break;
      case ENTER:
        this.props.onClick();
        break;
      case LEFT:
        this.reactSwipe.prev();
        break;
      case RIGHT:
        this.reactSwipe.next();
        break;
      default:
      // do nothing
    }
  }

  render() {
    const { t } = this.context;
    const {
      height,
      fallback,
      thumbnails,
      showArrows,
      showIndicator,
      transitionTime,
      clickForNext,
    } = this.props;

    // Dynamic content might be empty, use fallback
    if (!this.props.children) {
      return (
        <CarouselWrapper className="pj-ui-carousel">{fallback}</CarouselWrapper>
      );
    }

    const children = React.Children.toArray(this.props.children).filter(
      child => child
    );

    const childCount = React.Children.count(children);
    const singleItem = childCount === 1;

    const showIndicators = !thumbnails && showIndicator && childCount > 1;
    const showThumbnails = thumbnails && childCount > 1;
    const reactSwipeStyles = Carousel.getSwipeStyles();

    reactSwipeStyles.wrapper = {
      ...reactSwipeStyles.wrapper,
      maxHeight: '100%',
    };
    return (
      <CarouselWrapper
        ref={div => {
          this.carousel = div;
        }}
        role="button"
        tabIndex={0}
        className="pj-ui-carousel"
        onClick={e => this.handleOnClick(e)}
        onKeyDown={e => this.handleKeyDown(e)}
        style={{ minHeight: height }}
      >
        {showArrows && !singleItem && (
          <SwitchButton
            style={{ left: '3%' }}
            size="large"
            variant="icon"
            color="secondary"
            icon={<ChevronLeft />}
            ariaLabel={t('symbols', 'symbol.show-prev')}
            onClick={() => this.reactSwipe.prev()}
            data-test="SwitchButton"
          />
        )}

        {!singleItem ? (
          <ReactSwipe
            ref={reactSwipe => {
              this.reactSwipe = reactSwipe;
            }}
            key={childCount}
            style={reactSwipeStyles}
            swipeOptions={{
              auto: 0,
              startSlide: this.state.currentPosition,
              speed: transitionTime,
              disableScroll: false,
              continuous: true,
              callback: pos => {
                this.setState({
                  currentPosition: pos,
                });

                if (!this.disableOnChange) {
                  this.props.onChange(pos, SWIPE);
                }
                this.disableOnChange = false;
              },
            }}
          >
            {React.Children.map(children, (child, index) => (
              <CarouselSlide
                key={index} // eslint-disable-line react/no-array-index-key
                className="pj-ui-carousel--slide"
                style={{ maxHeight: height }}
              >
                <CarouselItem
                  inView={index === this.state.currentPosition}
                  item={child}
                  onClick={clickForNext ? () => this.reactSwipe.next() : null}
                />
              </CarouselSlide>
            ))}
          </ReactSwipe>
        ) : (
          <>{this.props.children}</>
        )}

        {showArrows && !singleItem && (
          <SwitchButton
            style={{ right: '3%' }}
            size="large"
            variant="icon"
            color="secondary"
            icon={<ChevronRight />}
            ariaLabel={t('symbols', 'symbol.show-next')}
            onClick={() => this.reactSwipe.next()}
            data-test="SwitchButton"
          />
        )}

        {showThumbnails && (
          <ThumbnailWrapper className="pj-ui-carousel--thumbnail-wrapper">
            <HorizontalScroll loadingHeight={80} itemWidth={90}>
              {thumbnails.map((thumbnail, index) => (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <Thumbnail
                  key={thumbnail}
                  role="button"
                  tabIndex={index}
                  onClick={() => this.setPosition(index, THUMBNAIL)}
                  className="pj-ui-carousel--thumbnail"
                  selected={index === this.state.currentPosition}
                >
                  {typeof thumbnail === 'string' ? (
                    <img src={thumbnail} alt="" />
                  ) : (
                    thumbnail
                  )}
                </Thumbnail>
              ))}
            </HorizontalScroll>
          </ThumbnailWrapper>
        )}

        {showIndicators && (
          <IndicatorWrapper
            ref={div => {
              this.indicatorWrapper = div;
            }}
            className="pj-ui-carousel--indicator-wrapper"
          >
            {children.map((_, index) => (
              // eslint-disable-next-line jsx-a11y/control-has-associated-label
              <Indicator
                key={index} // eslint-disable-line react/no-array-index-key
                type="button"
                onClick={() => this.setPosition(index, DOT)}
                selected={index === this.state.currentPosition}
                className="pj-ui-carousel--indicator"
              />
            ))}
          </IndicatorWrapper>
        )}
      </CarouselWrapper>
    );
  }
}

const { node, number, func, arrayOf, bool, string } = PropTypes;
Carousel.propTypes = {
  height: string.isRequired,
  focus: bool,
  showArrows: bool,
  showIndicator: bool,
  position: number,
  children: node,
  fallback: node,
  thumbnails: arrayOf(node),
  onClick: func,
  onChange: func,
  transitionTime: number,
  clickForNext: bool,
};

Carousel.defaultProps = {
  position: null,
  focus: false,
  showArrows: ['hybrid', 'mouseOnly'].includes(deviceType),
  showIndicator: true,
  children: null,
  fallback: null,
  thumbnails: null,
  onClick: noop,
  onChange: noop,
  transitionTime: 300,
  clickForNext: false,
};

Carousel.contextType = AppContext;

export default Carousel;
